import React from 'react';
import { useTranslation } from 'react-i18next';
// The portal's canonical switch (headless-ui + the shared switchContainer classes),
// reused here so the direction toggle looks like every other toggle in the app.
import CustomCheckbox from 'shared/components/forms/SchemaFormTemplates/CustomCheckbox';
import { AccountBundle } from '../flowMapTypes';
import { FlowArrow } from '../useFlowGraph';
import { Confirmation } from './ConfirmationModal';

const Toggle = ({
  label,
  on,
  onChange,
}: {
  label: string;
  on: boolean;
  onChange: () => void;
}) => (
  <span className="flex items-center gap-2">
    <span className="text-body-sm text-ui-text-primary-text">{label}</span>
    <CustomCheckbox label={label} formData={on} onChange={onChange} />
  </span>
);

// The on/off switch in an arrow's panel header. Going out it is the whole
// Route's switch — same gesture as disabling a reverse flow, one arrow to the
// right of it. Coming back it adds or removes the reverse Route.
const FlowSendingToggle = ({
  arrow,
  routeEnabled,
  providerBundle,
  destBundle,
  dest,
  providerAlsoProvidesFor,
  onToggleRouteEnabled,
  onEnableReverseFlow,
  onDisableReverseFlow,
  confirm,
}: {
  arrow: FlowArrow;
  routeEnabled: boolean;
  providerBundle: AccountBundle;
  destBundle: AccountBundle;
  dest: any;
  // How many other Routes the provider feeds; null when that lookup failed.
  providerAlsoProvidesFor?: number | null;
  onToggleRouteEnabled?: (enabled: boolean) => void;
  onEnableReverseFlow: (dest: any) => void;
  onDisableReverseFlow: (dest: any) => void;
  confirm: (confirmation: Confirmation) => void;
}) => {
  const { t } = useTranslation();
  if (arrow.direction === 'out') {
    if (!routeEnabled)
      return (
        <Toggle
          label={t('Enable')}
          on={false}
          onChange={() => onToggleRouteEnabled?.(true)}
        />
      );
    return (
      <Toggle
        label={t('Sending')}
        on={true}
        onChange={() =>
          confirm({
            title: t('Stop sending data'),
            // `enabled` is a field on the provider Connection, not on the
            // route record, so this stops every Route that Connection
            // feeds — don't claim it only affects this one.
            // null = the "which other Routes" lookup failed, so the
            // reassuring single-Route wording would be a guess.
            body:
              providerAlsoProvidesFor === null
                ? t(
                    'Stop sending data from {{name}}? This Connection may provide data to other Routes, which would stop too. The configuration itself is kept.',
                    { name: providerBundle.name },
                  )
                : (providerAlsoProvidesFor ?? 0) > 0
                  ? t(
                      'Stop sending data from {{name}}? It also provides data to {{count}} other Route, which stops too. The configuration itself is kept.',
                      {
                        name: providerBundle.name,
                        count: providerAlsoProvidesFor,
                      },
                    )
                  : t(
                      'Stop sending data from {{name}}? Data stops flowing to every destination of this Route. The configuration itself is kept.',
                      { name: providerBundle.name },
                    ),
            confirmLabel: t('Stop sending'),
            destructive: true,
            onConfirm: () => onToggleRouteEnabled?.(false),
          })
        }
      />
    );
  }
  if (arrow.state === 'enabled')
    return (
      <Toggle
        label={t('Sending')}
        on={true}
        onChange={() =>
          dest &&
          confirm({
            title: t('Stop sending data'),
            body: t(
              'Stop sending data from {{from}} to {{to}}? This removes the reverse Route; the configuration itself is kept.',
              { from: destBundle.name, to: providerBundle.name },
            ),
            confirmLabel: t('Stop sending'),
            destructive: true,
            onConfirm: () => {
              onDisableReverseFlow(dest);
            },
          })
        }
      />
    );
  if (arrow.state === 'disabled')
    return (
      <Toggle
        label={t('Enable')}
        on={false}
        onChange={() => {
          if (dest) onEnableReverseFlow(dest);
        }}
      />
    );
  return null;
};

export default FlowSendingToggle;
